import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import TableSkeleton from '../../components/TableSkeleton';
import { PlusIcon } from '../../components/IconButton/icons';
import ListPage, { SearchInput } from '../../components/ListPage';
import { useUserRole } from '../../utils/useUserRole';
import { isReadOnlyError, notifyReadOnly } from '../../utils/supabaseErrors';
import {
  CompetencyStatus,
  MatrixSection,
  MatrixSkill,
  SkillsMatrixData,
  cellKey,
  nextStatus,
} from './config';
import MatrixTable from './components/MatrixTable';
import SectionModal from './components/SectionModal';
import SkillModal from './components/SkillModal';
import './styles.scss';

const SkillsMatrix: React.FC = () => {
  const session = useSession();
  const supabase = useSupabaseClient();
  const { isManager } = useUserRole();

  const [data, setData] = useState<SkillsMatrixData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assignmentMap, setAssignmentMap] = useState<Record<string, CompetencyStatus>>({});
  const [pendingCells, setPendingCells] = useState<Set<string>>(new Set());
  const [myTeamMemberId, setMyTeamMemberId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [sectionModal, setSectionModal] = useState<{ open: boolean; section: MatrixSection | null }>({
    open: false,
    section: null,
  });
  const [skillModal, setSkillModal] = useState<{
    open: boolean;
    skill: MatrixSkill | null;
    defaultSectionId: number | null;
  }>({ open: false, skill: null, defaultSectionId: null });

  const fetchMatrix = useCallback(async (showSkeleton = false) => {
    if (showSkeleton) setLoading(true);
    try {
      const { data: matrix, error: rpcError } = await supabase.rpc('get_skills_matrix');
      if (rpcError) throw rpcError;
      const typed = matrix as SkillsMatrixData;
      setData(typed);
      const map: Record<string, CompetencyStatus> = {};
      typed.assignments.forEach(a => {
        map[cellKey(a.team_member_id, a.skill_id)] = a.status;
      });
      setAssignmentMap(map);
      setError(null);
    } catch (err) {
      console.error('Error loading skills matrix:', err);
      setError(err instanceof Error ? err.message : 'Failed to load the skills matrix.');
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    fetchMatrix(true);
  }, [fetchMatrix]);

  // Own team_member_id, recorded as assessed_by on every status change.
  useEffect(() => {
    const fetchOwnTeamMemberId = async () => {
      if (!session?.user) return;
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('team_member_id')
        .eq('id', session.user.id)
        .single();
      if (profileError) {
        console.error('Error fetching profile:', profileError);
      } else {
        setMyTeamMemberId(profile?.team_member_id ?? null);
      }
    };
    fetchOwnTeamMemberId();
  }, [session, supabase]);

  const getStatus = useCallback(
    (memberId: string, skillId: number): CompetencyStatus =>
      assignmentMap[cellKey(memberId, skillId)] ?? 'None',
    [assignmentMap]
  );

  const handleCycle = useCallback(async (memberId: string, skillId: number) => {
    if (!isManager) return;
    const key = cellKey(memberId, skillId);
    if (pendingCells.has(key)) return;

    const current = assignmentMap[key] ?? 'None';
    const next = nextStatus(current);

    setAssignmentMap(prev => ({ ...prev, [key]: next }));
    setPendingCells(prev => new Set(prev).add(key));

    // Omit assessed_by while the profile lookup is still in flight — sending
    // null here would overwrite the previous assessor on the conflict-update
    // path and destroy the audit trail.
    const payload: {
      team_member_id: string;
      skill_id: number;
      status: CompetencyStatus;
      assessed_by?: string;
    } = { team_member_id: memberId, skill_id: skillId, status: next };
    if (myTeamMemberId) payload.assessed_by = myTeamMemberId;

    const { error: upsertError } = await supabase
      .from('team_member_skills')
      .upsert(payload, { onConflict: 'team_member_id,skill_id' });

    setPendingCells(prev => {
      const updated = new Set(prev);
      updated.delete(key);
      return updated;
    });

    if (upsertError) {
      console.error('Error updating skill status:', upsertError);
      setAssignmentMap(prev => ({ ...prev, [key]: current }));
      if (isReadOnlyError(upsertError)) {
        notifyReadOnly(upsertError);
      } else {
        setError('Failed to save the status change. Please try again.');
      }
    } else {
      // A successful save clears any stale "failed to save" banner.
      setError(prev => (prev ? null : prev));
    }
  }, [isManager, pendingCells, assignmentMap, myTeamMemberId, supabase]);

  const handleModalSaved = useCallback(() => {
    fetchMatrix();
  }, [fetchMatrix]);

  // Client-side skill search: a section stays visible if its name matches, or
  // is narrowed to its matching skills (by name or key standards).
  const visibleSections = useMemo(() => {
    if (!data) return [];
    const query = searchTerm.trim().toLowerCase();
    if (!query) return data.sections;
    return data.sections
      .map(section => {
        // A name-matched section stays visible whole — even with no skills
        // yet, so a freshly added section can be found by search.
        if (section.name.toLowerCase().includes(query)) return section;
        const skills = section.skills.filter(skill =>
          skill.name.toLowerCase().includes(query) ||
          (skill.key_standards ?? '').toLowerCase().includes(query)
        );
        return skills.length > 0 ? { ...section, skills } : null;
      })
      .filter((section): section is MatrixSection => section !== null);
  }, [data, searchTerm]);

  return (
    <ListPage
      title="Skills Matrix"
      className="skills-matrix"
      search={
        <SearchInput
          placeholder="Search skills, standards…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClear={() => setSearchTerm('')}
          aria-label="Search skills"
        />
      }
      actions={
        isManager && (
          <>
            <button
              type="button"
              className="pill-btn pill-btn--sm"
              onClick={() => setSectionModal({ open: true, section: null })}
            >
              <PlusIcon /> Add section
            </button>
            <button
              type="button"
              className="pill-btn pill-btn--solid pill-btn--sm"
              onClick={() => setSkillModal({ open: true, skill: null, defaultSectionId: null })}
            >
              <PlusIcon /> Add skill
            </button>
          </>
        )
      }
    >
      {error && (
        <div className="list-page__error">
          {error}{' '}
          <button type="button" className="link-btn" onClick={() => fetchMatrix(true)}>Try again</button>
        </div>
      )}

      {loading ? (
        <TableSkeleton compact rows={14} columns={8} showHeader />
      ) : searchTerm.trim() && visibleSections.length === 0 ? (
        <div className="list-page__empty">No skills match your search.</div>
      ) : data && (
        <MatrixTable
          members={data.members}
          sections={visibleSections}
          getStatus={getStatus}
          pendingCells={pendingCells}
          canEdit={isManager}
          onCycle={handleCycle}
          onEditSection={(section) => setSectionModal({ open: true, section })}
          onEditSkill={(skill, section) =>
            setSkillModal({ open: true, skill, defaultSectionId: section.id })
          }
          onAddSkill={(section) =>
            setSkillModal({ open: true, skill: null, defaultSectionId: section.id })
          }
        />
      )}

      <SectionModal
        isOpen={sectionModal.open}
        onClose={() => setSectionModal({ open: false, section: null })}
        onSaved={handleModalSaved}
        section={sectionModal.section}
        sections={data?.sections ?? []}
      />

      <SkillModal
        isOpen={skillModal.open}
        onClose={() => setSkillModal({ open: false, skill: null, defaultSectionId: null })}
        onSaved={handleModalSaved}
        skill={skillModal.skill}
        defaultSectionId={skillModal.defaultSectionId}
        sections={data?.sections ?? []}
      />
    </ListPage>
  );
};

export default SkillsMatrix;
